import React, { FC, useEffect, useState } from "react";
import { Card, Collapse } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as CollapseSVG } from "go-core/images/svg/actions/collapse.svg";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { api } from "../../../../../../../services/Api/api";
import { UserApi } from "../../../../../../../services/Api/types";
import { SupportOrganizationApi } from "../../../../../../../services/support/types";

interface FormProps {
	search: string;
	company_id: string;
	tax_id_no: string;
	email: string;
	phone_number: string;
	organization_name: string;
	company_name: string;
	user_name: string;
	client_reference: string;
	parent_company: string;
}

interface Props {
	setOrganizations: (organizations: SupportOrganizationApi[]) => void;
	setUsers: (users: UserApi[]) => void;
	selectedSorts: string[];
}

const SearchOrganizationForm: FC<Props> = ({ setOrganizations, setUsers, selectedSorts }) => {
	const { search } = useLocation();
	const decodedSearch = decodeURIComponent(search);
	const form = useForm<FormProps>({
		criteriaMode: "all",
		defaultValues: {
			search: decodedSearch.slice(1),
		},
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [collapseIn, setCollapseIn] = useState(false);
	const { t } = useTranslation("admin");
	const { addFlash } = useFlash();
	const {
		register,
		handleSubmit,
		formState: { errors },
		getValues,
		control,
	} = form;
	const history = useHistory();
	const isMobile = useWindowSize().isMobile;

	useEffect(() => {
		if (getValues("search")) {
			onSubmit();
		}
	}, []);

	useEffect(() => {
		if (selectedSorts.length) onSubmit();
	}, [selectedSorts]);

	const processFilters = (data: FormProps) => {
		const filters: string[] = [];
		if (data.company_id) filters.push(`company_id|e=${data.company_id}`);
		if (data.tax_id_no) filters.push(`tax_id_no|c=${data.tax_id_no}`);
		if (data.email) filters.push(`email|c=${data.email}`);
		if (data.phone_number) filters.push(`phone_number|c=${data.phone_number}`);
		if (data.organization_name) filters.push(`organization_name|c=${data.organization_name}`);
		if (data.company_name) filters.push(`company_name|c=${data.company_name}`);
		if (data.user_name) filters.push(`user_name|c=${data.user_name}`);
		if (data.client_reference) filters.push(`+referencja_klienta.ORGANIZATION|e=${data.client_reference}`);
		if (data.parent_company) filters.push(`+parent_company.ORGANIZATION|c=${data.parent_company}`);

		return btoa(unescape(encodeURIComponent(NEW_WAY_TO_ENCODING_FILTER_SIGN + filters.join(FILTER_SEPARATOR))));
	};

	const onSubmit = handleSubmit(async (data: any) => {
		setLoading(true);
		try {
			const params: Record<string, any> = {};
			params.f = processFilters(data);
			history.push({
				search: encodeURIComponent(data.search),
			});
			if (selectedSorts.length) params.sort = selectedSorts.join(",");
			if (data.search) params.search = data.search;

			const res = await adminApi.searchSupportAccessOrganizations(params);
			setLoading(false);
			setOrganizations([...res.organizations]);
			setUsers([...res.users]);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	});

	const onCollapse = () => {
		const collapse = collapseIn;
		setCollapseIn(!collapse);
		const icon = document.getElementById("search-organization-collapse-icon");
		if (icon) {
			icon.style.transform = !collapse ? "rotate(180deg)" : "";
		}
	};

	const getClientReferenceOptions = async () => {
		const params = {
			resource_name: "ADMIN_ORGANIZATION",
			custom_field_resource_type: "ORGANIZATION",
			custom_field_permission_scope: "WRITE",
		};
		const { custom_fields } = await api.getResourceConfig(params);
		const clientReferenceCustomField = custom_fields.find(
			(customField) => customField.slug === "referencja_klienta"
		);

		if (!clientReferenceCustomField) return [];

		return clientReferenceCustomField.options;
	};

	return (
		<FormDirty
			isDirty={false}
			key="search-organization-form"
			onSubmit={onSubmit}
			className={`${isMobile ? "mt-2" : ""}`}
		>
			<FormInput
				name="search"
				errors={errors}
				label={t("common.word.search", { ns: "lib" })}
				register={register}
			/>
			<Card className="advanced-organization-search mb-4">
				<Card.Body>
					<div className="title" onClick={onCollapse}>
						<h5>{t("common.word.advanced", { ns: "lib" })}</h5>
						<CollapseSVG className="collapse-icon" id="search-organization-collapse-icon" />
					</div>
					<Collapse in={collapseIn}>
						<div>
							<FormInput
								name="company_id"
								errors={errors}
								label={t("common.word.company_id")}
								register={register}
							/>
							<FormInput
								name="tax_id_no"
								errors={errors}
								label={t("lib:common.word.tax_id_no.title")}
								register={register}
							/>
							<FormInput
								name="email"
								errors={errors}
								label={t("lib:common.word.email")}
								register={register}
							/>
							<FormInput
								name="phone_number"
								errors={errors}
								label={t("lib:common.word.phone_number")}
								register={register}
							/>
							<FormInput
								name="organization_name"
								errors={errors}
								label={t("modules.support.field.organization_name.title")}
								register={register}
							/>
							<FormInput
								name="company_name"
								errors={errors}
								label={t("common.word.company_name")}
								register={register}
							/>
							<FormInput
								name="user_name"
								errors={errors}
								label={t("modules.support.field.user_name.title")}
								register={register}
							/>
							<FormSelectGroup
								name="client_reference"
								errors={errors}
								getOptionLabel={(option) => option.value}
								label={t("modules.support.field.client_reference.title")}
								loadOptions={getClientReferenceOptions}
								control={control}
								isSearchable={false}
							/>
							<FormInput
								name="parent_company"
								errors={errors}
								label={"Parent company"}
								register={register}
							/>
						</div>
					</Collapse>
				</Card.Body>
			</Card>
			<ButtonLoading loading={loading} onClick={onSubmit} type="submit">
				{t("common.word.search", { ns: "lib" })}
			</ButtonLoading>
		</FormDirty>
	);
};

export default SearchOrganizationForm;
